// Rule Status Normalization Utility
// 5-level system for Security Framework (Socle) rule status:
// 0: À évaluer, 1: Non applicable (N/A), 2: Écart majeur, 3: Écart mineur, 4: Conforme

/**
 * Numeric status constants
 * @readonly
 * @enum {number}
 */
export const RULE_STATUS = {
  NA: 0,
  NOT_EFFECTIVE: 1,
  MAJOR_GAPS: 2,
  MINOR_GAPS: 3,
  COMPLETE: 4
};

/**
 * Backward compatibility mapping from old string values to new numeric values
 * @type {Object<string, number>}
 */
const LEGACY_STATUS_MAPPING = {
  'applied': RULE_STATUS.COMPLETE,
  'partially-applied': RULE_STATUS.MINOR_GAPS,
  'not-applied': RULE_STATUS.NOT_EFFECTIVE,
  'restricted': RULE_STATUS.MAJOR_GAPS,
  'certification': RULE_STATUS.COMPLETE
};

/**
 * Normalizes any status value (legacy string or new numeric) to the canonical numeric 0-4 value
 * @param {string|number|null|undefined} value - The status value to normalize
 * @returns {number} - The normalized numeric status (0-4), defaults to NOT_EFFECTIVE (1) if unknown
 */
export const normalizeRuleStatus = (value) => {
  // Already numeric
  if (typeof value === 'number') {
    if (value >= 0 && value <= 4) {
      return value;
    }
    return RULE_STATUS.NOT_EFFECTIVE; // default
  }

  // Null/undefined
  if (value === null || value === undefined || value === '') {
    return RULE_STATUS.NOT_EFFECTIVE; // default
  }

  // String value - check legacy mapping first
  const normalizedValue = String(value).toLowerCase().trim();
  
  if (normalizedValue in LEGACY_STATUS_MAPPING) {
    return LEGACY_STATUS_MAPPING[normalizedValue];
  }

  // Try parsing as number
  const parsed = parseInt(normalizedValue, 10);
  if (!isNaN(parsed) && parsed >= 0 && parsed <= 4) {
    return parsed;
  }

  // Unknown value - default to NOT_EFFECTIVE
  console.warn(`[ruleStatusNormalizer] Unknown status value "${value}", defaulting to NOT_EFFECTIVE (1)`);
  return RULE_STATUS.NOT_EFFECTIVE;
};

/**
 * Gets the status configuration (labels, colors) for a given status level
 * @param {number} level - The numeric status level (0-4)
 * @param {Function} [t] - Optional i18n translation function
 * @returns {Object} - Status configuration object
 */
export const getRuleStatusConfig = (level, t) => {
  // Ensure level is normalized
  const normalizedLevel = normalizeRuleStatus(level);

  // Base configurations with colors
  const configs = {
    [RULE_STATUS.COMPLETE]: {
      label: t ? t('securityFoundation.status.complete', 'Conforme') : 'Conforme',
      labelEn: 'Compliant',
      color: 'green',
      bgColor: 'bg-green-100',
      bgClass: 'bg-green-100',
      textColor: 'text-green-800',
      textClass: 'text-green-800',
      dotColor: 'text-green-500',
      borderColor: 'border-green-200',
      pdfColor: [34, 197, 94],      // Green RGB
      tailwindBg: 'bg-emerald-50',
      tailwindText: 'text-emerald-700',
      tailwindBorder: 'border-emerald-200'
    },
    [RULE_STATUS.MINOR_GAPS]: {
      label: t ? t('securityFoundation.status.minorGaps', 'Partiellement conforme') : 'Partiellement conforme',
      labelEn: 'Partially Compliant',
      color: 'yellow',
      bgColor: 'bg-yellow-100',
      bgClass: 'bg-yellow-100',
      textColor: 'text-yellow-800',
      textClass: 'text-yellow-800',
      dotColor: 'text-yellow-500',
      borderColor: 'border-yellow-200',
      pdfColor: [251, 191, 36],     // Yellow RGB
      tailwindBg: 'bg-amber-50',
      tailwindText: 'text-amber-700',
      tailwindBorder: 'border-amber-200'
    },
    [RULE_STATUS.MAJOR_GAPS]: {
      label: t ? t('securityFoundation.status.majorGaps', 'Non conforme') : 'Non conforme',
      labelEn: 'Major gap',
      color: 'red',
      bgColor: 'bg-red-100',
      bgClass: 'bg-red-100',
      textColor: 'text-red-800',
      textClass: 'text-red-800',
      dotColor: 'text-red-500',
      borderColor: 'border-red-200',
      pdfColor: [239, 68, 68],      // Red RGB
      tailwindBg: 'bg-red-50',
      tailwindText: 'text-red-700',
      tailwindBorder: 'border-red-200'
    },
    [RULE_STATUS.NOT_EFFECTIVE]: {
      label: t ? t('securityFoundation.status.notEffective', 'Non applicable (N/A)') : 'Non applicable (N/A)',
      labelEn: 'Not applicable (N/A)',
      color: 'gray',
      bgColor: 'bg-gray-100',
      bgClass: 'bg-gray-100',
      textColor: 'text-gray-800',
      textClass: 'text-gray-800',
      dotColor: 'text-gray-400',
      borderColor: 'border-gray-200',
      pdfColor: [156, 163, 175],    // Gray RGB
      tailwindBg: 'bg-gray-50',
      tailwindText: 'text-gray-600',
      tailwindBorder: 'border-gray-200'
    },
    [RULE_STATUS.NA]: {
      label: t ? t('securityFoundation.status.na', 'À évaluer') : 'À évaluer',
      labelEn: 'To Evaluate',
      color: 'gray',
      bgColor: 'bg-gray-100',
      bgClass: 'bg-gray-100',
      textColor: 'text-gray-800',
      textClass: 'text-gray-800',
      dotColor: 'text-gray-400',
      borderColor: 'border-gray-200',
      pdfColor: [156, 163, 175],    // Gray RGB
      tailwindBg: 'bg-gray-50',
      tailwindText: 'text-gray-600',
      tailwindBorder: 'border-gray-200'
    }
  };

  return configs[normalizedLevel] || configs[RULE_STATUS.NOT_EFFECTIVE];
};

/**
 * Gets the list of status options for building select/radio UI elements
 * @param {Function} t - i18n translation function
 * @returns {Array<{value: number, label: string}>} - Array of status options
 */
export const getRuleStatusOptions = (t) => {
  return [
    { value: RULE_STATUS.COMPLETE, label: t('securityFoundation.status.complete', 'Conforme') },
    { value: RULE_STATUS.MINOR_GAPS, label: t('securityFoundation.status.minorGaps', 'Écart mineur') },
    { value: RULE_STATUS.MAJOR_GAPS, label: t('securityFoundation.status.majorGaps', 'Écart majeur') },
    { value: RULE_STATUS.NOT_EFFECTIVE, label: t('securityFoundation.status.notEffective', 'Non applicable (N/A)') }
  ];
};

/**
 * Gets the status label for use in PDF/DOCX reports (French)
 * @param {number} level - The numeric status level (0-4)
 * @returns {string} - The French status label
 */
export const getRuleStatusLabelFr = (level) => {
  const labels = {
    [RULE_STATUS.COMPLETE]: 'Conforme',
    [RULE_STATUS.MINOR_GAPS]: 'Écart mineur',
    [RULE_STATUS.MAJOR_GAPS]: 'Écart majeur',
    [RULE_STATUS.NOT_EFFECTIVE]: 'Non applicable (N/A)',
    [RULE_STATUS.NA]: 'À évaluer'
  };
  return labels[normalizeRuleStatus(level)] || 'Inconnu';
};

/**
 * Gets the status label for use in PDF/DOCX reports (English)
 * @param {number} level - The numeric status level (0-4)
 * @returns {string} - The English status label
 */
export const getRuleStatusLabelEn = (level) => {
  const labels = {
    [RULE_STATUS.COMPLETE]: 'Compliant',
    [RULE_STATUS.MINOR_GAPS]: 'Minor gap',
    [RULE_STATUS.MAJOR_GAPS]: 'Major gap',
    [RULE_STATUS.NOT_EFFECTIVE]: 'Not applicable (N/A)',
    [RULE_STATUS.NA]: 'To Evaluate'
  };
  return labels[normalizeRuleStatus(level)] || 'Unknown';
};

/**
 * Gets the PDF RGB color array for a status level
 * @param {number} level - The numeric status level (0-4)
 * @returns {number[]} - RGB color array [r, g, b]
 */
export const getRuleStatusPdfColor = (level) => {
  return getRuleStatusConfig(level).pdfColor;
};

export default {
  RULE_STATUS,
  normalizeRuleStatus,
  getRuleStatusConfig,
  getRuleStatusOptions,
  getRuleStatusLabelFr,
  getRuleStatusLabelEn,
  getRuleStatusPdfColor
};
